import { Container, Title, Text, Stack, Paper, List, ThemeIcon, Badge, Group, Anchor } from "@mantine/core";
import { AlertCircle, Code, Users, Target, BrandDiscord } from "tabler-icons-react";

/**
 * About page explaining the ReCore Alpha project status and purpose.
 */
export default function About() {
    return (
        <Container size="md" py="xl">
            <Stack gap="lg">
                {/* Header */}
                <div>
                    <Group gap="sm" mb="xs">
                        <Title order={1}>ReCore Alpha</Title>
                        <Badge color="orange" size="lg" variant="light">Alpha</Badge>
                    </Group>
                    <Text c="dimmed" size="lg">
                        The next generation platform for Counter-Strike Confederation
                    </Text>
                </div>

                {/* What is ReCore */}
                <Paper p="lg" radius="md" withBorder>
                    <Group gap="sm" mb="md">
                        <ThemeIcon color="blue" size="lg" variant="light">
                            <Target size={20} />
                        </ThemeIcon>
                        <Title order={3}>What is ReCore?</Title>
                    </Group>
                    <Text>
                        ReCore is a complete rebuild of the CSC (Counter-Strike Confederation)
                        league management platform. This project aims to modernize and improve
                        every aspect of the league experience, from team management to match
                        scheduling and player statistics.
                    </Text>
                </Paper>

                {/* Alpha Status Warning */}
                <Paper p="lg" radius="md" withBorder style={{ borderColor: 'var(--mantine-color-orange-5)' }}>
                    <Group gap="sm" mb="md">
                        <ThemeIcon color="orange" size="lg" variant="light">
                            <AlertCircle size={20} />
                        </ThemeIcon>
                        <Title order={3}>Alpha Status</Title>
                    </Group>
                    <Text mb="md">
                        This platform is currently in <strong>active development</strong>. You may encounter:
                    </Text>
                    <List spacing="xs" icon={
                        <ThemeIcon color="orange" size={20} radius="xl" variant="light">
                            <AlertCircle size={12} />
                        </ThemeIcon>
                    }>
                        <List.Item>Incomplete features or missing functionality</List.Item>
                        <List.Item>Bugs or unexpected behavior</List.Item>
                        <List.Item>UI/UX changes without notice</List.Item>
                        <List.Item>Data that may be reset during development</List.Item>
                    </List>
                </Paper>

                {/* Tech Stack */}
                <Paper p="lg" radius="md" withBorder>
                    <Group gap="sm" mb="md">
                        <ThemeIcon color="violet" size="lg" variant="light">
                            <Code size={20} />
                        </ThemeIcon>
                        <Title order={3}>Built With Modern Tech</Title>
                    </Group>
                    <Text mb="md">
                        ReCore is built with a modern tech stack designed for performance and scalability:
                    </Text>
                    <List spacing="xs">
                        <List.Item><strong>Frontend:</strong> React, TypeScript, Mantine UI</List.Item>
                        <List.Item><strong>Backend:</strong> .NET 8, GraphQL (HotChocolate)</List.Item>
                        <List.Item><strong>Database:</strong> PostgreSQL</List.Item>
                        <List.Item><strong>Integration:</strong> Discord OAuth, Steam API</List.Item>
                    </List>
                </Paper>

                {/* Get Involved */}
                <Paper p="lg" radius="md" withBorder>
                    <Group gap="sm" mb="md">
                        <ThemeIcon color="green" size="lg" variant="light">
                            <Users size={20} />
                        </ThemeIcon>
                        <Title order={3}>Get Involved</Title>
                    </Group>
                    <Text mb="md">
                        Your feedback is invaluable during this alpha phase. If you encounter
                        issues or have suggestions, please let us know!
                    </Text>
                    <Group gap="md">
                        <Anchor
                            href="https://discord.com/invite/csc"
                            target="_blank"
                            style={{ display: 'flex', alignItems: 'center', gap: '4px' }}
                        >
                            <BrandDiscord size={16} />
                            Join our Discord
                        </Anchor>
                    </Group>
                </Paper>

                {/* Footer note */}
                <Text c="dimmed" size="sm" ta="center">
                    Thank you for being part of the CSC community and helping us build the future of the league.
                </Text>
            </Stack>
        </Container>
    );
}
